import { FormattedMessage, getTimestampRelativeFormat } from '@futrli/localisation';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Forecast } from '@futrli-sdk/predict';

import Icon from 'carbon-react/lib/components/icon';

import { importSheetPredictions } from '../../../../../../../core/api/external-sheets';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';
import { trackEvent } from '../../../../../../../core/tracker';
import { SyncModal } from '../../components/sync-modal/sync-modal';
import { usePredictionsInfoContext } from '../../../../../context/predictions-info-context';

import styles from './sync-button.scss';

export const SyncButton: FC = () => {
  const { fetchForecast, forecast, fetchForecastReport, isConsolidation } = usePredictionsInfoContext();
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSyncComplete, setIsSyncComplete] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState<boolean>(false);

  const syncIconStyle = clsx({
    [styles.iconSpin]: isLoading
  });

  const openSyncModal = () => setIsSyncModalOpen(true);
  const closeSyncModal = () => setIsSyncModalOpen(false);

  // run sync and update forecast
  const onSync = async () => {
    closeSyncModal();
    setIsLoading(true);
    setIsSyncComplete(false);

    // import predictions
    try {
      const result = await importSheetPredictions(workspaceId, organisationId, forecastId as string);
      if (result.error) {
        trackEvent(TRACKING_EVENTS.IMPORT_SYNC_FAILURE);
        setError(true);
        setIsLoading(false);
        return;
      }
    } catch {
      trackEvent(TRACKING_EVENTS.IMPORT_SYNC_FAILURE);
      setError(true);
      setIsLoading(false);
      return;
    }

    trackEvent(TRACKING_EVENTS.IMPORT_SYNC_SUCCESS);

    // update report data to include updated sheet predictions
    fetchForecastReport && fetchForecastReport('all');

    // get updated sync time
    try {
      fetchForecast && (await fetchForecast());
    } catch {
      setError(true);
      setIsLoading(false);
      return;
    }

    setError(false);
    setIsLoading(false);
    setIsSyncComplete(true);
  };

  if (isConsolidation || !forecast) {
    return null;
  }

  const orgForecast = forecast as Forecast;
  return (
    <div className={styles.syncWrapper}>
      {isSyncModalOpen && (
        <SyncModal
          forecastId={forecastId as string}
          organisationId={organisationId}
          workspaceId={workspaceId}
          onClose={closeSyncModal}
          onSync={onSync}
        />
      )}
      <div className={styles.syncSection}>
        <div className={styles.syncMessage}>
          {orgForecast?.import_sheet_last_sync_at ? (
            <React.Fragment>
              <FormattedMessage id="import.data_synced" defaultMessage="Data synced" />
              <span data-testid="import-sync-date">{'\u00a0'}</span>
              {getTimestampRelativeFormat(orgForecast.import_sheet_last_sync_at, Date.now(), 1, false, {
                sentenceCase: true
              })}
            </React.Fragment>
          ) : (
            <FormattedMessage id="import.not_synced" defaultMessage="Data has not yet been synced" />
          )}
        </div>

        <div className={styles.syncButton} data-testid="import-sync-button" onClick={openSyncModal}>
          <React.Fragment>
            <Icon type="sync" className={syncIconStyle} color="#08ccf8" />
            <FormattedMessage id="import.sync" defaultMessage="Sync" />
          </React.Fragment>
        </div>
      </div>

      {!isLoading && isSyncComplete && (
        <div className={clsx(styles.syncComplete, styles.success)} data-testid="import-sync-success">
          <Icon type="tick" color="#68c6ab" />
          <FormattedMessage id="import.sync_complete" defaultMessage="Sheet predictions synced." />
        </div>
      )}

      {!isLoading && error && (
        <div className={clsx(styles.syncComplete, styles.error)} data-testid="import-sync-error">
          <Icon type="error" color="#f07272" className={styles.errorIcon} />
          <FormattedMessage
            id="import.sync_error"
            defaultMessage="Error syncing predictions. If this persists please contact support."
          />
        </div>
      )}
    </div>
  );
};
